import useUpdateDocumentField from 'hooks/DocumentsRepository/useUpdateDocumentField';
import type {
  DocumentReferenceField,
  DocumentReferenceFieldTax,
  DocumentReferenceFieldTaxAssessment,
  DocumentReferenceFieldTaxPaymentReminder,
} from 'types/repository.types';
import DocumentRepositoryFieldItem from './DocumentRepositoryFieldItem';
import { useTranslation } from 'react-i18next';
import formatPeriodLabel from 'utils/formatPeriodLabel';
import TaxFieldValue from './DocumentRepositoryTaxFieldValue';
import { useMemo } from 'react';
import formatMoney from 'utils/formatMoney';

type DocumentRepositoryTaxPaymentFieldProps = {
  field: DocumentReferenceField & DocumentReferenceFieldTax;
  documentId: string;
  setBoundingBoxField: (field: string | null) => void;
};

const DocumentRepositoryTaxPaymentField = ({
  field,
  documentId,
  setBoundingBoxField,
}: DocumentRepositoryTaxPaymentFieldProps): JSX.Element | null => {
  const { t } = useTranslation();

  const { mutate: updateDocumentField, isLoading } =
    useUpdateDocumentField(documentId);

  const { iban, communication, deadline, amount } = field.value as
    | DocumentReferenceFieldTaxAssessment['value']
    | DocumentReferenceFieldTaxPaymentReminder['value'];

  const handleAccept = () => {
    // TODO:

    updateDocumentField({ ...field, status: 'accepted' });
  };

  const handleReject = () =>
    // TODO:
    updateDocumentField({ ...field, status: 'rejected' });

  const title = useMemo(() => {
    return (
      t(`taxes.filters.types.${field.value.type}`) +
      ' ' +
      formatPeriodLabel(field.value.period)
    );
  }, [field.value.type, field.value.period, t]);

  return (
    <DocumentRepositoryFieldItem
      field={field}
      onAccept={handleAccept}
      onReject={handleReject}
      isLoading={isLoading}
      title={title}
      acceptButtonLabel={t('repository.document_form.fields.accept')}
      rejectButtonLabel={t('repository.document_form.fields.reject')}
    >
      <div className="flex flex-col gap-2 mt-2">
        <TaxFieldValue
          setBoundingBoxField={setBoundingBoxField}
          label={t(`repository.document_form.fields.tax.iban`)}
          value={iban}
          fieldName="tax.iban"
        />
        <TaxFieldValue
          setBoundingBoxField={setBoundingBoxField}
          label={t(`repository.document_form.fields.tax.communication`)}
          value={communication}
          fieldName="tax.communication"
        />
        <TaxFieldValue
          setBoundingBoxField={setBoundingBoxField}
          label={t(`repository.document_form.fields.tax.amount`)}
          value={formatMoney({ amount: amount ?? 0, currency: 'EUR' })}
          fieldName="tax.amount"
        />
        <TaxFieldValue
          setBoundingBoxField={setBoundingBoxField}
          label={t(`repository.document_form.fields.tax.deadline`)}
          value={deadline}
          fieldName="tax.deadline"
        />
      </div>
    </DocumentRepositoryFieldItem>
  );
};

export default DocumentRepositoryTaxPaymentField;
